import { useAuth0 } from '@auth0/auth0-react';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';

import { BreakpointProvider, GLOBAL_STYLES_CSS } from '@electricjs/arc';

import { GlobalUIProvider } from './components/GlobalUIProvider';
import { IntakeFormProvider } from './components/IntakeFormProvider';
import PageSpinner from './components/PageSpinner';
import { StorefrontProvider } from './components/Storefront';
import { ThemeProvider } from './components/ThemeProvider';
import { TicketFlowProvider } from './components/TicketFlowContext';
import QUERIES from './constants/queries';
import { isAuthRedirectReloadInProgress } from './helpers/authRedirectReload';
import { isMockMode } from './mocks/utils/helpers';
import { store } from './redux/store';
import { authenticatedRoutes, unauthenticatedRoutes } from './router/router';
import useDestroyExistingSSOSession from './hooks/initialization/useDestroyExistingSSOSession';
import { MOCKED_FEATURE_FLAGS } from './constants/mockMode';

const AuthenticatedAppProviders = () => {
  return (
    <IntakeFormProvider>
      <StorefrontProvider>
        <TicketFlowProvider>
          <RouterProvider
            router={authenticatedRoutes}
            fallbackElement={<PageSpinner />}
          />
        </TicketFlowProvider>
      </StorefrontProvider>
    </IntakeFormProvider>
  );
};

const UnauthenticatedAppProviders = () => {
  return (
    <RouterProvider
      router={unauthenticatedRoutes}
      fallbackElement={<PageSpinner />}
    />
  );
};

const App = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  const { isDestroyingSession, hasDestroyedSession } =
    useDestroyExistingSSOSession();

  // Inject global styles from Arc on mount - it's a string so we need to create a style element and append it to the head
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.id = 'arc-global-styles';
    styleElement.textContent = GLOBAL_STYLES_CSS;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  if (
    isLoading ||
    isDestroyingSession ||
    hasDestroyedSession ||
    isAuthRedirectReloadInProgress()
  ) {
    return <PageSpinner />;
  }

  /**
   * We expose only the minimal providers required for both authenticated and unauthenticated users.
   */
  const clientSideID = window._env_?.VITE_LAUNCH_DARKLY_CLIENTSIDE_ID;

  return (
    <LDProvider
      clientSideID={clientSideID}
      options={
        isMockMode
          ? {
              bootstrap: MOCKED_FEATURE_FLAGS,
              streaming: false,
              sendEvents: false,
              diagnosticOptOut: true,
              fetchGoals: false,
            }
          : undefined
      }>
      <BreakpointProvider queries={QUERIES}>
        <ThemeProvider>
          <GlobalUIProvider>
            <ReduxProvider store={store}>
              {isAuthenticated ? (
                <AuthenticatedAppProviders />
              ) : (
                <UnauthenticatedAppProviders />
              )}
            </ReduxProvider>
          </GlobalUIProvider>
        </ThemeProvider>
      </BreakpointProvider>
    </LDProvider>
  );
};

export default App;
